import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

import {
    FaArrowRight,
    FaBookOpen,
    FaBullseye,
    FaChartLine,
    FaGraduationCap,
    FaLightbulb,
    FaPeopleArrows,
    FaRocket,
    FaUsers,
    FaUserTie,
    FaBriefcase,
    FaComments,
    FaHandshake,
} from "react-icons/fa";

const About = () => {
    useEffect(() => {
        AOS.init({
            duration: 900,
            once: true,
            offset: 80,
            easing: "ease-out-cubic",
        });
    }, []);

    const learningAreas = [
        {
            id: "01",
            title: "Leadership Development",
            category: "Leadership Training",
            description:
                "Develop the mindset, confidence, communication, decision-making, and people skills needed to lead with purpose and responsibility.",
            icon: FaUserTie,
        },
        {
            id: "02",
            title: "Career Development",
            category: "Professional Growth",
            description:
                "Build professional confidence, workplace readiness, career awareness, and practical skills for continued personal and professional growth.",
            icon: FaChartLine,
        },
        {
            id: "03",
            title: "Entrepreneurship",
            category: "Business & Enterprise",
            description:
                "Explore entrepreneurial thinking, opportunity recognition, business planning, innovation, and responsible enterprise development.",
            icon: FaLightbulb,
        },
        {
            id: "04",
            title: "People & Organizations",
            category: "Management & HR",
            description:
                "Strengthen teamwork, people management, workplace relationships, organizational communication, and professional leadership.",
            icon: FaUsers,
        },
        {
            id: "05",
            title: "Communication",
            category: "Professional Communication",
            description:
                "Develop effective public speaking, presentation, interpersonal communication, listening, and professional expression.",
            icon: FaComments,
        },
        {
            id: "06",
            title: "Strategic Leadership",
            category: "Strategy & Planning",
            description:
                "Build strategic thinking through goal setting, planning, prioritization, execution, evaluation, and responsible decision-making.",
            icon: FaBullseye,
        },
    ];

    const values = [
        {
            number: "01",
            title: "Learn",
            description:
                "Build relevant knowledge through practical, accessible, and purposeful learning experiences.",
            icon: FaBookOpen,
        },
        {
            number: "02",
            title: "Grow",
            description:
                "Turn knowledge into stronger skills, confidence, discipline, and professional capability.",
            icon: FaChartLine,
        },
        {
            number: "03",
            title: "Lead",
            description:
                "Develop the judgment, communication, accountability, and responsibility required to lead others.",
            icon: FaUserTie,
        },
        {
            number: "04",
            title: "Impact",
            description:
                "Apply learning to real situations and create meaningful results in work, business, and community life.",
            icon: FaBullseye,
        },
    ];

    const developmentAreas = [
        {
            title: "Leadership",
            description:
                "Build leadership confidence, decision-making ability, accountability, and people skills.",
            icon: FaUserTie,
        },
        {
            title: "Career Development",
            description:
                "Strengthen professional skills, workplace readiness, confidence, and career direction.",
            icon: FaBriefcase,
        },
        {
            title: "Entrepreneurship",
            description:
                "Encourage opportunity recognition, innovation, business thinking, and responsible enterprise.",
            icon: FaLightbulb,
        },
        {
            title: "Communication",
            description:
                "Develop public speaking, presentation, interpersonal communication, and professional expression.",
            icon: FaComments,
        },
        {
            title: "People Management",
            description:
                "Develop the skills needed to work with teams, manage relationships, and build healthy workplaces.",
            icon: FaHandshake,
        },
        {
            title: "Strategic Thinking",
            description:
                "Learn to set priorities, develop plans, evaluate opportunities, and move ideas toward action.",
            icon: FaBullseye,
        },
    ];

    const journey = [
        {
            number: "01",
            title: "Learn",
            description:
                "Engage with practical knowledge, ideas, examples, and experiences that connect learning to everyday challenges.",
            icon: FaBookOpen,
        },
        {
            number: "02",
            title: "Understand",
            description:
                "Reflect on new ideas and develop a deeper understanding of yourself, your responsibilities, and your environment.",
            icon: FaLightbulb,
        },
        {
            number: "03",
            title: "Apply",
            description:
                "Practice new skills through exercises, discussions, scenarios, projects, and real-world situations.",
            icon: FaPeopleArrows,
        },
        {
            number: "04",
            title: "Impact",
            description:
                "Use your growth to contribute, lead responsibly, improve results, and create positive change.",
            icon: FaRocket,
        },
    ];

    return (
        <main className="cayep-about-page">

            <section className="cayep-about-hero">

                <div className="cayep-hero-grid"></div>

                <div className="cayep-hero-orb cayep-orb-one"></div>
                <div className="cayep-hero-orb cayep-orb-two"></div>
                <div className="cayep-hero-orb cayep-orb-three"></div>

                <div
                    className="cayep-about-container"
                    data-aos="fade-up"
                >

                    <div className="cayep-hero-label">
                        <span></span>
                        ABOUT CAYEP
                        <span></span>
                    </div>

                    <h1>
                        Educate.
                        <strong>
                            Equip.
                        </strong>
                        Empower.
                    </h1>

                    <p>
                        CAYEP Leadership Training creates practical
                        learning experiences that help individuals
                        develop confidence, professional capability,
                        leadership skills, and the mindset needed to
                        create meaningful impact.
                    </p>

                    <div className="cayep-hero-actions">

                        <a
                            href="#learning"
                            className="cayep-primary-button"
                        >
                            Discover CAYEP
                            <FaArrowRight />
                        </a>

                        <div className="cayep-learning-status">
                            <span></span>
                            LEARNING IN MOTION
                        </div>

                    </div>

                    <div className="cayep-hero-stats">

                        <div>
                            <strong>
                                01
                            </strong>

                            <span>
                                Learn With Purpose
                            </span>
                        </div>

                        <div>
                            <strong>
                                02
                            </strong>

                            <span>
                                Develop Practical Skills
                            </span>
                        </div>

                        <div>
                            <strong>
                                03
                            </strong>

                            <span>
                                Lead With Impact
                            </span>
                        </div>

                    </div>

                </div>

                <div className="cayep-hero-word">
                    LEAD
                </div>

                <div className="cayep-hero-bottom-line"></div>

            </section>

            <section
                className="cayep-story-section"
                id="learning"
            >

                <div
                    className="cayep-about-container"
                    data-aos="fade-up"
                >

                    <div className="cayep-story-heading">

                        <div>

                            <span className="cayep-section-kicker">
                                WHO WE ARE
                            </span>

                            <h2>
                                Learning That
                                <span>
                                    Creates Direction.
                                </span>
                            </h2>

                        </div>

                        <p>
                            CAYEP brings together leadership
                            development, career preparation,
                            entrepreneurship, communication,
                            management, and strategic thinking to
                            create learning experiences that connect
                            knowledge with real-world action.
                        </p>

                    </div>

                    <div className="cayep-story-grid">

                        <div className="cayep-story-main">

                            <span className="cayep-story-number">
                                01
                            </span>

                            <FaGraduationCap />

                            <h3>
                                Education With
                                <span>
                                    Purpose.
                                </span>
                            </h3>

                            <p>
                                We believe training should do more
                                than deliver information. It should
                                help people think differently,
                                communicate effectively, solve
                                problems, make responsible decisions,
                                and confidently apply what they have
                                learned.
                            </p>

                        </div>

                        <div className="cayep-story-side">

                            <div className="cayep-story-mini">

                                <FaUsers />

                                <div>
                                    <strong>
                                        People First
                                    </strong>

                                    <p>
                                        Developing people who can
                                        work with, influence, and
                                        support others effectively.
                                    </p>
                                </div>

                            </div>

                            <div className="cayep-story-mini">

                                <FaBullseye />

                                <div>
                                    <strong>
                                        Purpose Driven
                                    </strong>

                                    <p>
                                        Connecting personal growth
                                        with meaningful goals and
                                        practical action.
                                    </p>
                                </div>

                            </div>

                            <div className="cayep-story-mini">

                                <FaRocket />

                                <div>
                                    <strong>
                                        Future Focused
                                    </strong>

                                    <p>
                                        Preparing learners for the
                                        opportunities and challenges
                                        of a changing world.
                                    </p>
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </section>

            <section className="cayep-learning-section">

                <div
                    className="cayep-about-container"
                    data-aos="fade-up"
                >

                    <div className="cayep-showcase-heading">

                        <div>

                            <span className="cayep-section-kicker">
                                OUR LEARNING AREAS
                            </span>

                            <h2>
                                Skills For
                                <span>
                                    Real Life.
                                </span>
                            </h2>

                        </div>

                        <p>
                            Our training areas are designed to
                            address the skills people need to grow
                            professionally, lead responsibly, build
                            businesses, communicate effectively, and
                            contribute meaningfully to organizations
                            and communities.
                        </p>

                    </div>

                    <div className="cayep-learning-grid">

                        {learningAreas.map(
                            (area, index) => {

                                const Icon = area.icon;

                                return (
                                    <article
                                        className="cayep-learning-card"
                                        key={area.id}
                                        data-aos="fade-up"
                                        data-aos-delay={
                                            index * 80
                                        }
                                    >

                                        <div className="cayep-learning-card-top">

                                            <span>
                                                {area.id}
                                            </span>

                                            <div>
                                                <Icon />
                                            </div>

                                        </div>

                                        <div className="cayep-learning-content">

                                            <span className="cayep-learning-category">
                                                {area.category}
                                            </span>

                                            <h3>
                                                {area.title}
                                            </h3>

                                            <p>
                                                {area.description}
                                            </p>

                                            <a
                                                href="#courses"
                                                className="cayep-learning-link"
                                            >
                                                Explore Training
                                                <FaArrowRight />
                                            </a>

                                        </div>

                                    </article>
                                );
                            }
                        )}

                    </div>

                </div>

            </section>

            <section className="cayep-values-section">

                <div
                    className="cayep-about-container"
                    data-aos="fade-up"
                >

                    <div className="cayep-values-heading">

                        <span className="cayep-section-kicker">
                            WHAT GUIDES US
                        </span>

                        <h2>
                            The CAYEP
                            <span>
                                Learning Philosophy.
                            </span>
                        </h2>

                        <p>
                            Every learning experience should
                            contribute to personal growth,
                            professional capability, responsible
                            leadership, and meaningful impact.
                        </p>

                    </div>

                    <div className="cayep-values-grid">

                        {values.map(
                            (value, index) => {

                                const Icon = value.icon;

                                return (
                                    <div
                                        className="cayep-value-item"
                                        key={value.number}
                                        data-aos="fade-up"
                                        data-aos-delay={
                                            index * 100
                                        }
                                    >

                                        <div className="cayep-value-icon">
                                            <Icon />
                                        </div>

                                        <span>
                                            {value.number}
                                        </span>

                                        <h3>
                                            {value.title}
                                        </h3>

                                        <p>
                                            {value.description}
                                        </p>

                                    </div>
                                );
                            }
                        )}

                    </div>

                </div>

            </section>

            <section className="cayep-programs-section">

                <div className="cayep-about-container">

                    <div
                        className="cayep-program-heading"
                        data-aos="fade-up"
                    >

                        <span className="cayep-section-kicker">
                            WHAT WE DEVELOP
                        </span>

                        <h2>
                            Beyond The
                            <span>
                                Classroom.
                            </span>
                        </h2>

                    </div>

                    <div className="cayep-program-grid">

                        {developmentAreas.map(
                            (area, index) => {

                                const Icon = area.icon;

                                return (
                                    <div
                                        className="cayep-program-card"
                                        key={area.title}
                                        data-aos="fade-up"
                                        data-aos-delay={
                                            index * 80
                                        }
                                    >

                                        <Icon />

                                        <h3>
                                            {area.title}
                                        </h3>

                                        <p>
                                            {area.description}
                                        </p>

                                    </div>
                                );
                            }
                        )}

                    </div>

                </div>

            </section>

            <section className="cayep-process-section">

                <div className="cayep-about-container">

                    <div
                        className="cayep-process-heading"
                        data-aos="fade-up"
                    >

                        <span className="cayep-section-kicker">
                            THE CAYEP JOURNEY
                        </span>

                        <h2>
                            From Learning
                            <span>
                                To Leadership.
                            </span>
                        </h2>

                    </div>

                    <div className="cayep-process-grid">

                        {journey.map(
                            (step, index) => {

                                const Icon = step.icon;

                                return (
                                    <div
                                        className="cayep-process-step"
                                        key={step.number}
                                        data-aos="fade-up"
                                        data-aos-delay={
                                            index * 100
                                        }
                                    >

                                        <span>
                                            {step.number}
                                        </span>

                                        <Icon />

                                        <h3>
                                            {step.title}
                                        </h3>

                                        <p>
                                            {step.description}
                                        </p>

                                    </div>
                                );
                            }
                        )}

                    </div>

                </div>

            </section>

            <section
                className="cayep-about-cta"
                data-aos="fade-up"
            >

                <div className="cayep-cta-glow"></div>

                <div className="cayep-cta-inner">

                    <div className="cayep-cta-icon">
                        <FaGraduationCap />
                    </div>

                    <div>

                        <span>
                            YOUR NEXT STEP STARTS HERE
                        </span>

                        <h2>
                            Learn Today.
                            <strong>
                                Lead Tomorrow.
                            </strong>
                        </h2>

                        <p>
                            Explore CAYEP training programs and
                            discover learning opportunities designed
                            to strengthen your leadership, career,
                            business, communication, and professional
                            skills.
                        </p>

                    </div>

                    <a
                        href="#courses"
                        className="cayep-cta-button"
                    >
                        Explore Courses
                        <FaArrowRight />
                    </a>

                </div>

            </section>

            <style>{`

                .cayep-about-page {
                    --navy: #062A4B;
                    --deep-blue: #063A6B;
                    --black: #0A0F12;
                    --gold: #F6C944;
                    --golden-yellow: #EFA81C;
                    --orange: #C0750D;
                    --green: #4FAE22;
                    --olive-green: #5F6622;
                    --sky-blue: #0F6DAB;
                    --white: #F0EDEA;
                    --light-gray: #C4C7C7;
                    --antique-gold: #CBA454;
                    --soft-blue: #EAF2F7;
                    --soft-gold: #FFF8DC;
                    --border: #DDE3E6;
                    --text-gray: #61717C;

                    width: 100%;
                    overflow: hidden;
                    background: #ffffff;
                    color: var(--navy);
                    font-family:
                        "Poppins",
                        "Inter",
                        Arial,
                        sans-serif;
                }

                .cayep-about-container {
                    width: min(1380px, 92%);
                    margin: 0 auto;
                }

                .cayep-about-hero {
                    position: relative;
                    min-height: 720px;
                    display: flex;
                    align-items: center;
                    overflow: hidden;
                    background:
                        radial-gradient(
                            circle at 78% 25%,
                            rgba(246, 201, 68, 0.18),
                            transparent 23%
                        ),
                        radial-gradient(
                            circle at 15% 85%,
                            rgba(15, 109, 171, 0.08),
                            transparent 25%
                        ),
                        linear-gradient(
                            135deg,
                            #ffffff 0%,
                            #F0EDEA 60%,
                            #EAF2F7 100%
                        );
                }

                .cayep-hero-grid {
                    position: absolute;
                    inset: 0;
                    opacity: 0.45;
                    background-image:
                        linear-gradient(
                            rgba(6, 42, 75, 0.07) 1px,
                            transparent 1px
                        ),
                        linear-gradient(
                            90deg,
                            rgba(6, 42, 75, 0.07) 1px,
                            transparent 1px
                        );
                    background-size: 75px 75px;
                    mask-image:
                        linear-gradient(
                            to right,
                            black,
                            transparent 88%
                        );
                }

                .cayep-hero-orb {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(90px);
                    pointer-events: none;
                    animation:
                        cayepOrbMove
                        9s ease-in-out infinite;
                }

                .cayep-orb-one {
                    width: 390px;
                    height: 390px;
                    right: -120px;
                    top: -110px;
                    background: var(--gold);
                    opacity: 0.13;
                }

                .cayep-orb-two {
                    width: 260px;
                    height: 260px;
                    left: -100px;
                    bottom: -100px;
                    background: var(--sky-blue);
                    opacity: 0.08;
                    animation-delay: 2s;
                }

                .cayep-orb-three {
                    width: 180px;
                    height: 180px;
                    right: 24%;
                    bottom: -100px;
                    background: var(--green);
                    opacity: 0.06;
                    animation-delay: 4s;
                }

                @keyframes cayepOrbMove {

                    0%,
                    100% {
                        transform:
                            translate(0, 0)
                            scale(1);
                    }

                    50% {
                        transform:
                            translate(-30px, 25px)
                            scale(1.1);
                    }

                }

                .cayep-about-hero
                    .cayep-about-container {
                    position: relative;
                    z-index: 3;
                    padding: 110px 0;
                }

                .cayep-hero-label {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 25px;
                    color: var(--orange);
                    font-size: 10px;
                    font-weight: 800;
                    letter-spacing: 4px;
                }

                .cayep-hero-label span {
                    width: 44px;
                    height: 2px;
                    background: var(--golden-yellow);
                }

                .cayep-hero-label span:last-child {
                    width: 10px;
                }

                .cayep-about-hero h1 {
                    max-width: 850px;
                    margin: 0;
                    color: var(--navy);
                    font-size:
                        clamp(
                            65px,
                            9vw,
                            125px
                        );
                    line-height: 0.88;
                    font-weight: 800;
                    letter-spacing: -7px;
                }

                .cayep-about-hero h1 strong {
                    display: block;
                    color: var(--orange);
                }

                .cayep-about-hero p {
                    max-width: 760px;
                    margin: 38px 0 0;
                    color: var(--text-gray);
                    font-size: 16px;
                    line-height: 1.9;
                }

                .cayep-hero-actions {
                    display: flex;
                    align-items: center;
                    gap: 28px;
                    margin-top: 38px;
                }

                .cayep-primary-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 13px;
                    padding: 16px 24px;
                    background: var(--navy);
                    color: #ffffff;
                    font-size: 10px;
                    font-weight: 800;
                    text-decoration: none;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    transition: 0.3s ease;
                }

                .cayep-primary-button:hover {
                    background: var(--orange);
                    transform: translateY(-3px);
                    box-shadow:
                        0 15px 30px
                        rgba(192, 117, 13, 0.18);
                }

                .cayep-learning-status {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    color: var(--olive-green);
                    font-size: 9px;
                    font-weight: 800;
                    letter-spacing: 2px;
                }

                .cayep-learning-status span {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: var(--green);
                    box-shadow:
                        0 0 0 5px
                        rgba(79, 174, 34, 0.1);
                    animation:
                        cayepPulse
                        2s ease-in-out infinite;
                }

                @keyframes cayepPulse {

                    0%,
                    100% {
                        transform: scale(1);
                        opacity: 1;
                    }

                    50% {
                        transform: scale(1.35);
                        opacity: 0.65;
                    }

                }

                .cayep-hero-stats {
                    display: flex;
                    gap: 65px;
                    margin-top: 58px;
                }

                .cayep-hero-stats div {
                    display: flex;
                    flex-direction: column;
                }

                .cayep-hero-stats strong {
                    color: var(--navy);
                    font-size: 27px;
                    line-height: 1;
                }

                .cayep-hero-stats span {
                    max-width: 125px;
                    margin-top: 8px;
                    color: #7B878E;
                    font-size: 9px;
                    font-weight: 700;
                    line-height: 1.5;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                }

                .cayep-hero-word {
                    position: absolute;
                    right: -50px;
                    top: 50%;
                    z-index: 1;
                    color: rgba(6, 42, 75, 0.035);
                    font-size:
                        clamp(
                            150px,
                            25vw,
                            390px
                        );
                    font-weight: 900;
                    line-height: 1;
                    letter-spacing: -20px;
                    transform:
                        translateY(-50%)
                        rotate(-8deg);
                    pointer-events: none;
                }

                .cayep-hero-bottom-line {
                    position: absolute;
                    left: 0;
                    bottom: 0;
                    width: 100%;
                    height: 4px;
                    background:
                        linear-gradient(
                            90deg,
                            var(--navy),
                            var(--gold),
                            var(--orange),
                            var(--green),
                            transparent
                        );
                }

                .cayep-story-section {
                    padding: 125px 0;
                    background: #ffffff;
                }

                .cayep-story-heading {
                    display: grid;
                    grid-template-columns:
                        minmax(0, 1fr)
                        minmax(300px, 0.65fr);
                    gap: 80px;
                    align-items: end;
                    margin-bottom: 60px;
                }

                .cayep-section-kicker {
                    display: inline-block;
                    color: var(--orange);
                    font-size: 10px;
                    font-weight: 800;
                    letter-spacing: 3px;
                }

                .cayep-story-heading h2,
                .cayep-showcase-heading h2,
                .cayep-values-heading h2,
                .cayep-process-heading h2,
                .cayep-program-heading h2 {
                    margin: 12px 0 0;
                    color: var(--navy);
                    font-size:
                        clamp(
                            40px,
                            5vw,
                            68px
                        );
                    line-height: 0.98;
                    letter-spacing: -4px;
                }

                .cayep-story-heading h2 span,
                .cayep-showcase-heading h2 span,
                .cayep-values-heading h2 span,
                .cayep-process-heading h2 span,
                .cayep-program-heading h2 span {
                    display: block;
                    color: var(--light-gray);
                }

                .cayep-story-heading p,
                .cayep-showcase-heading p {
                    margin: 0;
                    color: var(--text-gray);
                    font-size: 14px;
                    line-height: 1.9;
                }

                .cayep-story-grid {
                    display: grid;
                    grid-template-columns:
                        minmax(0, 1.15fr)
                        minmax(300px, 0.85fr);
                    gap: 20px;
                }

                .cayep-story-main {
                    position: relative;
                    min-height: 420px;
                    padding: 48px;
                    overflow: hidden;
                    background:
                        linear-gradient(
                            135deg,
                            var(--navy),
                            var(--deep-blue)
                        );
                    color: #ffffff;
                }

                .cayep-story-main::before {
                    position: absolute;
                    content: "";
                    width: 300px;
                    height: 300px;
                    right: -130px;
                    bottom: -150px;
                    border-radius: 50%;
                    background: var(--gold);
                    opacity: 0.12;
                    filter: blur(10px);
                }

                .cayep-story-main > span {
                    position: absolute;
                    top: 30px;
                    right: 30px;
                    color: rgba(
                        255,
                        255,
                        255,
                        0.35
                    );
                    font-family: monospace;
                    font-size: 12px;
                }

                .cayep-story-main > svg {
                    margin-top: 55px;
                    color: var(--gold);
                    font-size: 42px;
                }

                .cayep-story-main h3 {
                    max-width: 500px;
                    margin: 30px 0 18px;
                    font-size: 42px;
                    line-height: 1;
                    letter-spacing: -2px;
                }

                .cayep-story-main h3 span {
                    display: block;
                    color: var(--gold);
                }

                .cayep-story-main p {
                    max-width: 620px;
                    margin: 0;
                    color: rgba(
                        255,
                        255,
                        255,
                        0.72
                    );
                    font-size: 13px;
                    line-height: 1.9;
                }

                .cayep-story-side {
                    display: grid;
                    gap: 1px;
                    background: var(--border);
                    border: 1px solid var(--border);
                }

                .cayep-story-mini {
                    display: grid;
                    grid-template-columns: 58px 1fr;
                    gap: 22px;
                    align-items: start;
                    padding: 32px;
                    background: #ffffff;
                    transition: 0.3s ease;
                }

                .cayep-story-mini:hover {
                    background: #F8FBFC;
                    transform: translateX(5px);
                }

                .cayep-story-mini > svg {
                    width: 52px;
                    height: 52px;
                    padding: 15px;
                    background: var(--soft-gold);
                    color: var(--orange);
                    font-size: 18px;
                }

                .cayep-story-mini strong {
                    display: block;
                    color: var(--navy);
                    font-size: 17px;
                }

                .cayep-story-mini p {
                    margin: 8px 0 0;
                    color: var(--text-gray);
                    font-size: 11px;
                    line-height: 1.75;
                }

                .cayep-learning-section {
                    padding: 125px 0;
                    background:
                        linear-gradient(
                            135deg,
                            #F5F8FA,
                            #F0EDEA
                        );
                    border-top: 1px solid var(--border);
                    border-bottom: 1px solid var(--border);
                }

                .cayep-showcase-heading {
                    display: grid;
                    grid-template-columns:
                        minmax(0, 1fr)
                        minmax(300px, 0.65fr);
                    gap: 80px;
                    align-items: end;
                    margin-bottom: 50px;
                }

                .cayep-learning-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(3, minmax(0, 1fr));
                    gap: 18px;
                }

                .cayep-learning-card {
                    position: relative;
                    min-height: 350px;
                    overflow: hidden;
                    border: 1px solid var(--border);
                    background: #ffffff;
                    box-shadow:
                        0 12px 40px
                        rgba(
                            6,
                            42,
                            75,
                            0.055
                        );
                    transition:
                        transform 0.4s ease,
                        box-shadow 0.4s ease,
                        border-color 0.4s ease;
                }

                .cayep-learning-card:hover {
                    transform: translateY(-8px);
                    border-color:
                        rgba(
                            192,
                            117,
                            13,
                            0.4
                        );
                    box-shadow:
                        0 25px 55px
                        rgba(
                            6,
                            42,
                            75,
                            0.1
                        );
                }

                .cayep-learning-card-top {
                    position: relative;
                    height: 125px;
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    padding: 25px;
                    overflow: hidden;
                    background:
                        linear-gradient(
                            135deg,
                            var(--navy),
                            var(--deep-blue)
                        );
                }

                .cayep-learning-card-top::before {
                    position: absolute;
                    content: "";
                    width: 180px;
                    height: 180px;
                    right: -70px;
                    bottom: -100px;
                    border-radius: 50%;
                    background: var(--gold);
                    opacity: 0.16;
                }

                .cayep-learning-card-top > span {
                    position: relative;
                    z-index: 1;
                    color: rgba(
                        255,
                        255,
                        255,
                        0.45
                    );
                    font-family: monospace;
                    font-size: 12px;
                }

                .cayep-learning-card-top > div {
                    position: relative;
                    z-index: 1;
                    width: 47px;
                    height: 47px;
                    display: grid;
                    place-items: center;
                    background: var(--gold);
                    color: var(--navy);
                    font-size: 18px;
                    transition: 0.3s ease;
                }

                .cayep-learning-card:hover
                    .cayep-learning-card-top > div {
                    background: var(--orange);
                    color: #ffffff;
                    transform: rotate(-5deg);
                }

                .cayep-learning-content {
                    padding: 28px;
                }

                .cayep-learning-category {
                    color: var(--orange);
                    font-size: 9px;
                    font-weight: 800;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                }

                .cayep-learning-content h3 {
                    margin: 9px 0 12px;
                    color: var(--navy);
                    font-size: 21px;
                    line-height: 1.2;
                }

                .cayep-learning-content p {
                    min-height: 105px;
                    margin: 0;
                    color: var(--text-gray);
                    font-size: 11px;
                    line-height: 1.8;
                }

                .cayep-learning-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 9px;
                    margin-top: 20px;
                    color: var(--navy);
                    font-size: 9px;
                    font-weight: 800;
                    text-decoration: none;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    transition: 0.3s ease;
                }

                .cayep-learning-link svg {
                    color: var(--orange);
                    transition: 0.3s ease;
                }

                .cayep-learning-link:hover {
                    color: var(--orange);
                }

                .cayep-learning-link:hover svg {
                    transform: translateX(5px);
                }

                .cayep-values-section {
                    padding: 125px 0;
                    background: #ffffff;
                }

                .cayep-values-heading {
                    max-width: 760px;
                    margin-bottom: 55px;
                }

                .cayep-values-heading p {
                    max-width: 650px;
                    margin-top: 25px;
                    color: var(--text-gray);
                    font-size: 14px;
                    line-height: 1.9;
                }

                .cayep-values-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(4, minmax(0, 1fr));
                    gap: 1px;
                    overflow: hidden;
                    border: 1px solid var(--border);
                    background: var(--border);
                }

                .cayep-value-item {
                    position: relative;
                    min-height: 330px;
                    padding: 35px;
                    background: #ffffff;
                    transition: 0.35s ease;
                }

                .cayep-value-item:hover {
                    background: #F8FBFC;
                }

                .cayep-value-icon {
                    width: 52px;
                    height: 52px;
                    display: grid;
                    place-items: center;
                    margin-bottom: 32px;
                    background: var(--soft-gold);
                    color: var(--orange);
                    font-size: 19px;
                }

                .cayep-value-item > span {
                    position: absolute;
                    top: 28px;
                    right: 25px;
                    color: #D6DDE0;
                    font-family: monospace;
                    font-size: 12px;
                }

                .cayep-value-item h3 {
                    margin: 0 0 13px;
                    color: var(--navy);
                    font-size: 25px;
                }

                .cayep-value-item p {
                    margin: 0;
                    color: var(--text-gray);
                    font-size: 11px;
                    line-height: 1.85;
                }

                .cayep-value-item::after {
                    position: absolute;
                    content: "";
                    left: 0;
                    bottom: 0;
                    width: 0;
                    height: 4px;
                    background:
                        linear-gradient(
                            90deg,
                            var(--navy),
                            var(--gold),
                            var(--orange)
                        );
                    transition: width 0.4s ease;
                }

                .cayep-value-item:hover::after {
                    width: 100%;
                }

                .cayep-programs-section {
                    padding: 125px 0;
                    background:
                        linear-gradient(
                            135deg,
                            #F0EDEA,
                            #EAF2F7
                        );
                }

                .cayep-program-heading {
                    margin-bottom: 55px;
                }

                .cayep-program-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(3, minmax(0, 1fr));
                    gap: 16px;
                }

                .cayep-program-card {
                    min-height: 270px;
                    padding: 35px;
                    border: 1px solid var(--border);
                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.82
                        );
                    transition: 0.35s ease;
                }

                .cayep-program-card:hover {
                    transform: translateY(-6px);
                    background: #ffffff;
                    border-color:
                        rgba(
                            246,
                            201,
                            68,
                            0.7
                        );
                    box-shadow:
                        0 20px 45px
                        rgba(
                            6,
                            42,
                            75,
                            0.07
                        );
                }

                .cayep-program-card > svg {
                    color: var(--orange);
                    font-size: 25px;
                }

                .cayep-program-card h3 {
                    margin: 28px 0 11px;
                    color: var(--navy);
                    font-size: 20px;
                }

                .cayep-program-card p {
                    margin: 0;
                    color: var(--text-gray);
                    font-size: 11px;
                    line-height: 1.85;
                }

                .cayep-process-section {
                    padding: 125px 0;
                    background: #ffffff;
                }

                .cayep-process-heading {
                    margin-bottom: 55px;
                }

                .cayep-process-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(4, minmax(0, 1fr));
                    border-top: 1px solid var(--border);
                    border-left: 1px solid var(--border);
                }

                .cayep-process-step {
                    position: relative;
                    min-height: 310px;
                    padding: 35px;
                    overflow: hidden;
                    border-right: 1px solid var(--border);
                    border-bottom: 1px solid var(--border);
                }

                .cayep-process-step > span {
                    position: absolute;
                    top: 25px;
                    right: 25px;
                    color: #D8DEE1;
                    font-family: monospace;
                    font-size: 12px;
                }

                .cayep-process-step > svg {
                    margin-top: 40px;
                    color: var(--orange);
                    font-size: 27px;
                }

                .cayep-process-step h3 {
                    margin: 28px 0 11px;
                    color: var(--navy);
                    font-size: 22px;
                }

                .cayep-process-step p {
                    margin: 0;
                    color: var(--text-gray);
                    font-size: 11px;
                    line-height: 1.85;
                }

                .cayep-process-step::after {
                    position: absolute;
                    content: "";
                    left: 0;
                    bottom: 0;
                    width: 0;
                    height: 4px;
                    background:
                        linear-gradient(
                            90deg,
                            var(--navy),
                            var(--gold),
                            var(--orange)
                        );
                    transition: width 0.4s ease;
                }

                .cayep-process-step:hover::after {
                    width: 100%;
                }

                .cayep-about-cta {
                    position: relative;
                    overflow: hidden;
                    margin: 0 0 60px;
                    background:
                        linear-gradient(
                            120deg,
                            var(--navy),
                            var(--deep-blue)
                        );
                    color: #ffffff;
                }

                .cayep-cta-glow {
                    position: absolute;
                    width: 500px;
                    height: 500px;
                    right: 5%;
                    top: -320px;
                    border-radius: 50%;
                    background: var(--gold);
                    filter: blur(100px);
                    opacity: 0.13;
                }

                .cayep-cta-inner {
                    position: relative;
                    z-index: 2;
                    width: min(1380px, 92%);
                    min-height: 280px;
                    margin: 0 auto;
                    display: grid;
                    grid-template-columns:
                        auto 1fr auto;
                    gap: 30px;
                    align-items: center;
                    padding: 48px 0;
                }

                .cayep-cta-icon {
                    width: 68px;
                    height: 68px;
                    display: grid;
                    place-items: center;
                    border: 1px solid
                        rgba(
                            246,
                            201,
                            68,
                            0.5
                        );
                    color: var(--gold);
                    font-size: 23px;
                }

                .cayep-cta-inner > div:nth-child(2) span {
                    color: var(--gold);
                    font-size: 9px;
                    font-weight: 800;
                    letter-spacing: 3px;
                }

                .cayep-cta-inner h2 {
                    margin: 8px 0;
                    font-size: 32px;
                    line-height: 1.15;
                }

                .cayep-cta-inner h2 strong {
                    color: var(--gold);
                }

                .cayep-cta-inner p {
                    max-width: 680px;
                    margin: 0;
                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.64
                        );
                    font-size: 12px;
                    line-height: 1.8;
                }

                .cayep-cta-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 13px;
                    padding: 17px 24px;
                    background: var(--gold);
                    color: var(--navy);
                    font-size: 10px;
                    font-weight: 800;
                    text-decoration: none;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    transition: 0.3s ease;
                }

                .cayep-cta-button:hover {
                    background: #ffffff;
                    transform: translateY(-3px);
                    box-shadow:
                        0 15px 30px
                        rgba(
                            246,
                            201,
                            68,
                            0.2
                        );
                }

                @media (max-width: 1100px) {

                    .cayep-learning-grid {
                        grid-template-columns:
                            repeat(2, minmax(0, 1fr));
                    }

                    .cayep-values-grid {
                        grid-template-columns:
                            repeat(2, minmax(0, 1fr));
                    }

                    .cayep-process-grid {
                        grid-template-columns:
                            repeat(2, minmax(0, 1fr));
                    }

                    .cayep-program-grid {
                        grid-template-columns:
                            repeat(2, minmax(0, 1fr));
                    }

                }

                @media (max-width: 768px) {

                    .cayep-about-hero {
                        min-height: 670px;
                    }

                    .cayep-about-hero
                        .cayep-about-container {
                        padding: 85px 0;
                    }

                    .cayep-about-hero h1 {
                        font-size: 62px;
                        letter-spacing: -4px;
                    }

                    .cayep-about-hero p {
                        font-size: 14px;
                    }

                    .cayep-hero-actions {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .cayep-hero-stats {
                        gap: 28px;
                    }

                    .cayep-story-section,
                    .cayep-learning-section,
                    .cayep-values-section,
                    .cayep-programs-section,
                    .cayep-process-section {
                        padding: 85px 0;
                    }

                    .cayep-story-heading,
                    .cayep-showcase-heading {
                        display: block;
                        margin-bottom: 38px;
                    }

                    .cayep-story-heading p,
                    .cayep-showcase-heading p {
                        margin-top: 25px;
                    }

                    .cayep-story-grid {
                        grid-template-columns: 1fr;
                    }

                    .cayep-story-main {
                        min-height: 390px;
                        padding: 35px;
                    }

                    .cayep-story-main h3 {
                        font-size: 35px;
                    }

                    .cayep-learning-grid {
                        grid-template-columns: 1fr;
                    }

                    .cayep-learning-card {
                        min-height: auto;
                    }

                    .cayep-values-grid,
                    .cayep-program-grid,
                    .cayep-process-grid {
                        grid-template-columns: 1fr;
                    }

                    .cayep-cta-inner {
                        grid-template-columns: 1fr;
                        gap: 22px;
                        padding: 45px 0;
                    }

                    .cayep-cta-button {
                        width: max-content;
                    }

                }

                @media (max-width: 480px) {

                    .cayep-about-container {
                        width: 90%;
                    }

                    .cayep-about-hero h1 {
                        font-size: 51px;
                        letter-spacing: -3px;
                    }

                    .cayep-about-hero p {
                        font-size: 13px;
                    }

                    .cayep-hero-stats {
                        display: grid;
                        grid-template-columns:
                            repeat(3, 1fr);
                        gap: 14px;
                    }

                    .cayep-hero-stats strong {
                        font-size: 20px;
                    }

                    .cayep-hero-stats span {
                        font-size: 7px;
                    }

                    .cayep-story-heading h2,
                    .cayep-showcase-heading h2,
                    .cayep-values-heading h2,
                    .cayep-process-heading h2,
                    .cayep-program-heading h2 {
                        font-size: 40px;
                        letter-spacing: -2px;
                    }

                    .cayep-story-main {
                        padding: 28px;
                    }

                    .cayep-story-main h3 {
                        font-size: 32px;
                    }

                    .cayep-story-mini {
                        padding: 25px;
                    }

                    .cayep-value-item,
                    .cayep-program-card {
                        min-height: 260px;
                        padding: 28px;
                    }

                    .cayep-process-step {
                        min-height: 270px;
                        padding: 28px;
                    }

                    .cayep-cta-inner h2 {
                        font-size: 26px;
                    }

                }

                @media (
                    prefers-reduced-motion: reduce
                ) {

                    .cayep-about-page *,
                    .cayep-about-page
                        *::before,
                    .cayep-about-page
                        *::after {
                        animation-duration:
                            0.01ms !important;

                        animation-iteration-count:
                            1 !important;

                        scroll-behavior:
                            auto !important;

                        transition-duration:
                            0.01ms !important;
                    }

                }

            `}</style>

        </main>
    );
};

export default About;